import Text from 'components/Text/Text';
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useArchivedMultiSelect } from 'hooks/useArchivedMultiSelect';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	setIDsOfRestaurantsWhereToHide: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	IDsOfRestaurantsWhereToHide: string[];
	IDsSelectedRestaurant: string[];
	initialIDsOfRestaurantsWhereToHide: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	setIDsOfRestaurantsWhereToHide,
	IDSelectedCustomer,
	IDsOfRestaurantsWhereToHide,
	IDsSelectedRestaurant,
	initialIDsOfRestaurantsWhereToHide,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const [searchString, setSearchString] = useState<string>('');

	const restToHide = useMemo(
		() => data.filter((el) => IDsSelectedRestaurant.includes(el.id)),
		[data, IDsSelectedRestaurant],
	);

	const { showArchived, toggleShowArchived, visibleItems, options } = useArchivedMultiSelect({
		items: restToHide,
		entityType: 'restaurants',
		selectedIds: IDsOfRestaurantsWhereToHide,
	});

	const filteredData = useMemo(() => {
		return searchString
			? options.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	}, [searchString, options]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
		return filteredData.map((el) => ({
			...el,
			disabled: !hasAccess && !accessibleRestaurantIds?.some((id) => id === el.id),
		}));
	}, [filteredData, loggedUser, hasAccess]);

	const handleSelectAllData = useCallback(() => {
		if (hasAccess) {
			if (visibleItems.length && IDsOfRestaurantsWhereToHide.length === visibleItems.length) {
				setIDsOfRestaurantsWhereToHide([]);
			} else {
				setIDsOfRestaurantsWhereToHide(visibleItems.map((el) => el.id));
			}
		} else {
			const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
			const visibleIds = visibleItems.map((el) => el.id);
			const filterAccessibleRestaurantIds = accessibleRestaurantIds.filter((el) => visibleIds.includes(el));
			const isCheckedAll = filterAccessibleRestaurantIds.length
				? filterAccessibleRestaurantIds.every((el) => IDsOfRestaurantsWhereToHide.includes(el))
				: false;
			if (isCheckedAll) {
				const filterIsDisabledInRestaurants = initialIDsOfRestaurantsWhereToHide.filter(
					(el) => !accessibleRestaurantIds.includes(el),
				);
				setIDsOfRestaurantsWhereToHide(filterIsDisabledInRestaurants);
			} else {
				const filteredLoggedUserRestaurants = loggedUser.restaurants
					? loggedUser.restaurants?.filter((el) => filterAccessibleRestaurantIds.includes(el.id))
					: [];
				const filterFilteredLoggedUserRestaurants = filteredLoggedUserRestaurants.filter(
					(el) => !IDsOfRestaurantsWhereToHide.includes(el.id),
				);
				setIDsOfRestaurantsWhereToHide([
					...IDsOfRestaurantsWhereToHide,
					...filterFilteredLoggedUserRestaurants.map((el) => el.id),
				]);
			}
		}
	}, [
		hasAccess,
		IDsOfRestaurantsWhereToHide,
		visibleItems,
		setIDsOfRestaurantsWhereToHide,
		loggedUser,
		initialIDsOfRestaurantsWhereToHide,
	]);

	useEffect(() => {
		setSearchString('');
	}, [IDSelectedCustomer, IDsSelectedRestaurant]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const getIsCheckedByHasAccess = () => {
				return !!(visibleItems.length && visibleItems.length === IDsOfRestaurantsWhereToHide.length);
			};

			const getIsCheckedByWithoutAccess = () => {
				const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				const visibleIds = visibleItems.map((el) => el.id);
				const filterAccessibleRestaurantIds = accessibleRestaurantIds.filter((el) => visibleIds.includes(el));
				return filterAccessibleRestaurantIds.length
					? filterAccessibleRestaurantIds.every((el) => IDsOfRestaurantsWhereToHide.includes(el))
					: false;
			};

			const getDisabledStatus = () => {
				const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				const visibleIds = visibleItems.map((el) => el.id);
				return visibleIds.some((el) => accessibleRestaurantIds.includes(el));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
							disabled={hasAccess ? !visibleItems.length : !getDisabledStatus()}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleItems,
			IDsOfRestaurantsWhereToHide,
			loggedUser,
			hasAccess,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			testId="MultipleSelect"
			name="IDsOfRestaurantsWhereToHide"
			label="restaurantsLabel"
			options={mappedOptions}
			disabled={!IDsSelectedRestaurant.length}
			onChange={setIDsOfRestaurantsWhereToHide}
			mode="multiple"
			counterValue={IDsOfRestaurantsWhereToHide.length}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(MultipleSelect);
